import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import Map from 'ol/Map';

@Injectable()
export class MapOlCesiumService {
    private ol3d: any = null;
    private cesiumLoaded = false;
    private cesiumLoading: Promise<void> | null = null;

    constructor(private $log: NGXLogger) {}

    /**
     * Dynamically load self-hosted CesiumJS (lazy loaded from local assets)
     * CSS is bundled in main styles, JS loads on demand
     */
    private loadCesiumAssets(): Promise<void> {
        if (this.cesiumLoaded) {
            return Promise.resolve();
        }
        if (this.cesiumLoading) {
            return this.cesiumLoading;
        }

        this.cesiumLoading = new Promise<void>((resolve, reject) => {
            // Set Cesium base URL to local assets
            (window as any).CESIUM_BASE_URL = './assets/cesium/';

            // Load self-hosted Cesium JS (lazy loaded)
            const script = document.createElement('script');
            script.src = './assets/cesium/Cesium.js';
            script.onload = () => {
                this.cesiumLoaded = true;
                this.$log.info('CesiumJS loaded successfully from local assets');
                resolve();
            };
            script.onerror = () => {
                this.cesiumLoading = null;
                this.$log.error('Failed to load self-hosted CesiumJS');
                reject(new Error('Failed to load CesiumJS from assets'));
            };
            document.head.appendChild(script);
        });

        return this.cesiumLoading;
    }

    /**
     * Initialize ol-cesium using the provided OpenLayers map
     */
    async init(map: Map): Promise<void> {
        try {
            // Load Cesium from CDN first
            await this.loadCesiumAssets();

            // Now Cesium is available as window.Cesium
            // Dynamic import of olcs - it will use the global Cesium
            const OlCesiumModule = await import('olcs' as any);
            const OlCesium = OlCesiumModule.default || OlCesiumModule.OlCesium || OlCesiumModule;

            // Create OlCesium instance wrapping the existing OL map
            this.ol3d = new OlCesium({ map });

            // Configure Cesium scene
            const scene = this.ol3d.getCesiumScene();
            scene.globe.depthTestAgainstTerrain = true;
            
            // Optional: Set camera constraints
            scene.screenSpaceCameraController.maximumZoomDistance = 20000000;

            this.$log.info('OlCesium initialized successfully');
        } catch (error) {
            this.$log.error('Error initializing OlCesium: ' + (error as any).message);
            throw error;
        }
    }

    /**
     * Toggle between 2D (OpenLayers) and 3D (Cesium) mode
     */
    toggle3D(): boolean {
        if (!this.ol3d) {
            this.$log.warn('OlCesium not initialized');
            return false;
        }
        const enabled = !this.ol3d.getEnabled();
        this.ol3d.setEnabled(enabled);
        this.$log.info('3D mode: ' + (enabled ? 'ON' : 'OFF'));
        return enabled;
    }

    /**
     * Enable 3D mode
     */
    enable3D(): void {
        if (this.ol3d) {
            this.ol3d.setEnabled(true);
            this.$log.info('3D mode enabled');
        }
    }

    /**
     * Disable 3D mode (back to 2D OpenLayers)
     */
    disable3D(): void {
        if (this.ol3d) {
            this.ol3d.setEnabled(false);
            this.$log.info('3D mode disabled');
        }
    }

    /**
     * Check if 3D mode is currently enabled
     */
    is3DEnabled(): boolean {
        return this.ol3d ? this.ol3d.getEnabled() : false;
    }

    /**
     * Get the OlCesium instance for advanced usage
     */
    getOl3d(): any {
        return this.ol3d;
    }

    /**
     * Get the Cesium scene for direct manipulation
     */
    getCesiumScene(): any {
        return this.ol3d ? this.ol3d.getCesiumScene() : null;
    }

    /**
     * Cleanup when destroying the map
     */
    destroy(): void {
        if (this.ol3d) {
            this.ol3d.setEnabled(false);
            this.ol3d = null;
            this.$log.info('OlCesium destroyed');
        }
        this.cesiumLoaded = false;
        this.cesiumLoading = null;
    }
}
